import React from "react";
import { Users, User } from "lucide-react";
import { cn } from "../lib/utils";
import { handwritten } from "./editorial";

// The in-app mockups: five little CSS renderings of what each tab looks like.
//
// Extracted from Features.tsx (2026-08-01) so /how-it-works can use the SAME
// ones rather than a second, worse illustration of the same five screens. That
// page previously showed a lucide icon on a tinted square inside a polaroid
// frame, which said "here is an icon" where these say "here is the product".
// They are markup rather than screenshots on purpose: they restyle with the
// theme, stay sharp at any size, cost no image bytes, and never go stale
// against a build of the app.
//
// Exported individually because the two pages want them in different
// containers: Features wraps each in its own tinted panel, how-it-works drops
// them straight onto the section ground.

export function HappeningCardMock({
  emoji,
  title,
  when,
  where,
  loopedIn,
  rotate,
}: {
  emoji: string;
  title: string;
  when: string;
  where: string;
  loopedIn: number;
  rotate: string;
}) {
  return (
    <div
      className={cn(
        "bg-canvas border border-ink/15 rounded-2xl p-4 shadow-[0_14px_36px_-14px_rgba(5,5,5,0.18)] transform-gpu w-full max-w-[280px]",
        rotate,
      )}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="text-3xl">{emoji}</div>
        <div className="flex-1">
          <p className="font-serif text-lg leading-tight text-ink">{title}</p>
          <p className="text-xs text-gray-500 mt-1">
            {when} · {where}
          </p>
        </div>
      </div>
      <div className="flex items-center justify-between pt-3 border-t border-ink/10">
        <div className="flex -space-x-1.5">
          {Array.from({ length: Math.min(loopedIn, 4) }).map((_, i) => (
            <div
              key={i}
              className="w-6 h-6 rounded-full border-2 border-canvas"
              style={{
                background: ["#FFA07A", "#C9A1C9", "#8B92E8", "#9DD9D6"][i % 4],
              }}
            />
          ))}
        </div>
        <span className="text-xs font-semibold text-ink">
          {loopedIn} looped in
        </span>
      </div>
    </div>
  );
}

export function FeedVisual() {
  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center gap-4">
      <HappeningCardMock
        emoji="☕"
        title="Coffee at Monmouth"
        when="In 20 min"
        where="Covent Garden"
        loopedIn={4}
        rotate="-rotate-1"
      />
      <HappeningCardMock
        emoji="🏃"
        title="Sunday morning run"
        when="Sun 9am"
        where="Hackney Marshes"
        loopedIn={7}
        rotate="rotate-1"
      />
      <HappeningCardMock
        emoji="🍕"
        title="Pizza after work"
        when="Fri 7pm"
        where="Borough Market"
        loopedIn={3}
        rotate="-rotate-2"
      />
    </div>
  );
}

export function MapVisual() {
  // Robust grid map mock: aspect-square container with predictable pin
  // percentages. No SVG <pattern> (it does not render reliably across
  // mobile webviews) — grid lines are CSS linear-gradients on the
  // background-image. No nested `absolute inset-4` that collapses on
  // small viewports.
  return (
    <div className="w-full h-full flex items-center justify-center px-2">
      <div
        className="relative w-full max-w-[340px] aspect-square rounded-2xl border border-ink/15 shadow-[0_14px_36px_-14px_rgba(5,5,5,0.18)] overflow-hidden"
        style={{
          backgroundColor: "var(--color-paper-salmon)",
          backgroundImage:
            "linear-gradient(to right, rgba(5,5,5,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(5,5,5,0.08) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      >
        {/* Privacy zone around "you" */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-28 h-28 rounded-full bg-salmon/15 border-2 border-dashed border-salmon" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-salmon border border-ink/15 z-10" />

        {/* Pin 1 — coffee (top-left) */}
        <div className="absolute top-[18%] left-[20%] flex flex-col items-center">
          <div className="bg-ink text-canvas px-2.5 py-1 rounded-full text-base font-bold leading-none">
            ☕
          </div>
          <div className="w-2 h-2 bg-ink rotate-45 -mt-1" />
        </div>

        {/* Pin 2 — run (bottom-right) */}
        <div className="absolute top-[68%] right-[18%] flex flex-col items-center">
          <div className="bg-ink text-canvas px-2.5 py-1 rounded-full text-base font-bold leading-none">
            🏃
          </div>
          <div className="w-2 h-2 bg-ink rotate-45 -mt-1" />
        </div>

        {/* Pin 3 — pizza (bottom-left) */}
        <div className="absolute top-[72%] left-[28%] flex flex-col items-center">
          <div className="bg-ink text-canvas px-2.5 py-1 rounded-full text-base font-bold leading-none">
            🍕
          </div>
          <div className="w-2 h-2 bg-ink rotate-45 -mt-1" />
        </div>

        {/* Pin 4 — picnic (top-right) */}
        <div className="absolute top-[24%] right-[22%] flex flex-col items-center">
          <div className="bg-ink text-canvas px-2.5 py-1 rounded-full text-base font-bold leading-none">
            🧺
          </div>
          <div className="w-2 h-2 bg-ink rotate-45 -mt-1" />
        </div>

        <p
          className="absolute bottom-3 left-4 text-sm text-ink/60 leading-none"
          style={handwritten}
        >
          you (blurred)
        </p>
        <p className="absolute top-3 right-4 text-xs uppercase tracking-widest text-ink/40 font-bold">
          Map
        </p>
      </div>
    </div>
  );
}

export function CreateVisual() {
  const steps = [
    {
      n: "1",
      label: "Add a photo",
      icon: "📸",
      rotate: "-rotate-2",
    },
    {
      n: "2",
      label: "Drop a pin",
      icon: "📍",
      rotate: "rotate-1",
    },
    {
      n: "3",
      label: "Say what time",
      icon: "⏰",
      rotate: "-rotate-1",
    },
  ];
  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center gap-3">
      {steps.map(({ n, label, icon, rotate }) => (
        <div
          key={n}
          className={cn(
            "bg-canvas border border-ink/15 rounded-2xl p-4 pr-6 shadow-[0_14px_36px_-14px_rgba(5,5,5,0.18)] flex items-center gap-4 w-full max-w-[280px] transform-gpu",
            rotate,
          )}
        >
          <div className="w-10 h-10 rounded-full bg-salmon border border-ink/15 flex items-center justify-center font-serif font-bold text-xl text-ink">
            {n}
          </div>
          <div className="flex-1">
            <p className="font-semibold text-ink">{label}</p>
            <p className="text-xs text-gray-500">~10 seconds</p>
          </div>
          <span className="text-2xl">{icon}</span>
        </div>
      ))}
      <p className="text-2xl text-salmon-dark mt-2" style={handwritten}>
        that&rsquo;s it.
      </p>
    </div>
  );
}

export function CommunitiesVisual() {
  const communities = [
    { name: "Hackney Run Club", count: 184, emoji: "🏃", rotate: "-rotate-3" },
    { name: "London Book Crew", count: 92, emoji: "📚", rotate: "rotate-2" },
    { name: "Sunday Picnic Co.", count: 56, emoji: "🧺", rotate: "-rotate-1" },
  ];
  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center gap-4">
      {communities.map((c) => (
        <div
          key={c.name}
          className={cn(
            "bg-canvas border border-ink/15 rounded-2xl p-4 shadow-[0_14px_36px_-14px_rgba(5,5,5,0.18)] flex items-center gap-4 w-full max-w-[300px] transform-gpu",
            c.rotate,
          )}
        >
          <div
            className="w-12 h-12 rounded-xl border border-ink/15 flex items-center justify-center text-2xl"
            style={{ backgroundColor: "var(--color-paper-salmon)" }}
          >
            {c.emoji}
          </div>
          <div className="flex-1">
            <p className="font-serif text-lg leading-tight text-ink">
              {c.name}
            </p>
            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
              <Users size={11} />
              {c.count} members · 3 loops this week
            </p>
          </div>
          <div className="bg-ink text-canvas px-3 py-1.5 rounded-full text-xs font-bold">
            Joined
          </div>
        </div>
      ))}
    </div>
  );
}

export function YouVisual() {
  const looped = [
    { name: "Maya", color: "#FFA07A", events: 5 },
    { name: "Theo", color: "#C9A1C9", events: 4 },
    { name: "Nia", color: "#8B92E8", events: 3 },
    { name: "Sam", color: "#9DD9D6", events: 3 },
    { name: "Joon", color: "#FFA07A", events: 2 },
    { name: "Alex", color: "#C9A1C9", events: 2 },
  ];
  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center gap-4 px-4">
      <div className="flex items-center justify-between w-full max-w-[320px] mb-2">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-salmon border border-ink/15 flex items-center justify-center">
            <User size={16} className="text-ink" />
          </div>
          <div>
            <p className="font-serif text-base text-ink leading-tight">you</p>
            <p className="text-[10px] text-gray-500">14 loops · 2 hosted</p>
          </div>
        </div>
      </div>

      <div className="bg-canvas border border-ink/15 rounded-2xl p-5 w-full max-w-[320px] shadow-[0_14px_36px_-14px_rgba(5,5,5,0.18)]">
        <p className="text-xs uppercase tracking-widest text-ink/50 mb-3 font-bold">
          Looped with
        </p>
        <div className="grid grid-cols-3 gap-3">
          {looped.map((p) => (
            <div key={p.name} className="flex flex-col items-center gap-1">
              <div
                className="w-12 h-12 rounded-full border border-ink/15 flex items-center justify-center font-serif font-bold text-canvas"
                style={{ backgroundColor: p.color }}
              >
                {p.name[0]}
              </div>
              <p className="text-xs font-semibold text-ink">{p.name}</p>
              <p className="text-[9px] text-gray-500">×{p.events}</p>
            </div>
          ))}
        </div>
      </div>

      <p className="text-2xl text-salmon-dark" style={handwritten}>
        the regulars
      </p>
    </div>
  );
}
